import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useReducer,
  ReactElement,
} from 'react';

import useProcedureSearchColumns from './useProcedureSearchColumns';

import { HighlightTextWrapper } from 'ui/patterns';

import { useGetAdvancedProcedureSearch } from 'hooks';

import { showErrorToast } from 'utils/showToast';
import {
  procedureColumnData,
  searchItem,
  sortItem,
  procedureInitialSate,
} from 'utils/advanceSearchUtils';

import {
  ProcedureSearchParams,
  SearchRuleItem,
  ProcedureOptionsType,
} from '../../advancedSearchTypes';

import useAdvancedSearchStore from 'store/useAdvancedSearchStore';

function reducer(
  state: ProcedureOptionsType,
  action: {
    type: keyof ProcedureOptionsType | 'reset';
    newState?: ProcedureOptionsType;
  },
) {
  switch (action.type) {
    case 'procedureText': {
      return {
        ...state,
        procedureText: !state.procedureText,
      };
    }
    case 'procedureTitle': {
      return {
        ...state,
        procedureTitle: !state.procedureTitle,
      };
    }
    case 'isGlobal':
      return { ...state, isGlobal: !state.isGlobal };
    case 'isInvisible':
      return { ...state, isInvisible: !state.isInvisible };
    case 'includeHidden':
      return { ...state, includeHidden: !state.includeHidden };
    case 'reset':
      if (action.newState) return { ...action.newState };
      return { ...procedureInitialSate };
    default:
      throw new Error();
  }
}

const initialSearchList: Array<SearchRuleItem> = [{ ...searchItem }];

export default function useProcedureSearch() {
  // search block states
  const [searchList, setSearchList] = useState<Array<SearchRuleItem>>([
    ...initialSearchList.map(item => item),
  ]);
  const [searchParams, setSearchParams] = useState<ProcedureSearchParams>();
  const [columnsSearch, setColumnsSearch] = useState({}); // header search
  const [currentSortItem, setCurrentSortItem] = useState(sortItem);

  //checkbox states
  const [procedureOptions, setProcedureOptions] = useReducer(reducer, {
    ...procedureInitialSate,
  });

  const { searchStore, setParams } = useAdvancedSearchStore();

  useEffect(() => {
    if (searchStore) {
      if (searchStore.procedureSearchParams) {
        const {
          isGlobal,
          procedureText,
          procedureTitle,
          isInvisible,
          includeHidden,
          searchList,
        } = searchStore.procedureSearchParams;
        setProcedureOptions({
          type: 'reset',
          newState: {
            isGlobal,
            procedureText,
            procedureTitle,
            isInvisible,
            includeHidden,
          },
        });
        setSearchList(searchList);
      }
      if (searchStore.sortItem) {
        setCurrentSortItem(searchStore.sortItem);
      }
      if (searchStore.columnSearch) {
        setColumnsSearch(searchStore.columnSearch);
      }
      setSearchParams(searchStore.procedureSearchParams);
    }
  }, [searchStore]);

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
    isLoading: advancedSearchloading,
  } = useGetAdvancedProcedureSearch(
    searchParams,
    columnsSearch,
    currentSortItem,
  );

  const tableData = useMemo(() => {
    if (data) {
      return data.pages
        .map((dataItem: any) => dataItem?.Data?.Data)
        .flat()
        .filter((item: any) => !!item);
    }
    return;
  }, [data]);

  const currentRecords = useMemo(() => {
    if (data) {
      return data.pages.map((dataItem: any) => dataItem?.Data?.Data)?.flat();
    }
  }, [data]);

  const totalRecords = useMemo(() => {
    if (data) {
      return data.pages[0] && data.pages[0]?.Data?.TotalRecords;
    }
    return 0;
  }, [data]);

  const csvImportData = tableData?.length ? tableData : [];

  const procedureDataCsv = [
    [
      'CID',
      'PID',
      'SID',
      'AccountName',
      'ProcedureName',
      'RelationshipManagerName',
    ],
    ...csvImportData.map(
      ({
        CID,
        PID,
        SID,
        AccountName,
        ProcedureName,
        RelationshipManagerName,
      }: any) => [
        CID,
        PID,
        SID,
        AccountName,
        ProcedureName,
        RelationshipManagerName,
      ],
    ),
  ];

  const onEndReached = () => {
    if (hasNextPage) fetchNextPage();
  };

  const columnList = useProcedureSearchColumns([
    ...procedureColumnData.slice(),
  ]);

  const headerUpdateCallBack = useCallback(
    ({ columnId, value }: { columnId: string; value: string }) => {
      setColumnsSearch({
        ...columnsSearch,
        [columnId]: value,
      });
    },
    [columnsSearch],
  );

  const handleSortChange = useCallback(
    (item: string): void => {
      let sortOrder = 'asc';
      if (
        currentSortItem.sortKey === item &&
        currentSortItem.sortOrder === 'asc'
      ) {
        sortOrder = 'desc';
      }
      setCurrentSortItem({
        sortKey: item,
        sortOrder,
      });
    },
    [setCurrentSortItem, currentSortItem],
  );

  const resetProcedureSearch = () => {
    setSearchList([{ ...searchItem }]);
    setColumnsSearch({});
    setCurrentSortItem(sortItem);
    setProcedureOptions({ type: 'reset' });
    setSearchParams(undefined);
  };

  const toggleProcedureOption = (key?: keyof ProcedureOptionsType) => () => {
    if (!key) return setProcedureOptions({ type: 'reset' });
    return setProcedureOptions({ type: key });
  };

  function searchButtonClick() {
    if (searchList.filter(item => item.value.trim().length === 0).length > 0) {
      showErrorToast({
        message: 'Please Enter a keyword',
      });
    } else {
      setSearchParams({
        searchList: JSON.parse(JSON.stringify(searchList.slice())),
        ...procedureOptions,
      });
    }
  }

  function onClickToolTip(
    path: string,
    params: Record<string, number | string>,
  ) {
    setParams({
      procedureSearchParams: searchParams,
      columnSearch: columnsSearch,
      sortItem: currentSortItem,
    });
    localStorage.setItem('fromSearch', JSON.stringify(params?.Pid));
    window.open(path);
  }

  function getHighlightedText(content: string): ReactElement {
    let newString = [content];
    searchList?.forEach(searchItem => {
      const arr: string[] = [];
      newString?.forEach(x => {
        if (x !== undefined)
          arr?.push(...x?.split(new RegExp(`(${searchItem.value})`, 'gi')));
      });
      newString = [...arr];
    });

    return (
      <>
        <HighlightTextWrapper content={content} searchedWords={searchList} />
      </>
    );
  }

  return {
    procedureDataCsv,
    totalProcedureRecordsCount: totalRecords,
    listedProcedureRecordCount: currentRecords,
    isProcedureSearchFetching: isFetching,
    procedureOptions,
    toggleProcedureOption,
    resetProcedureSearch,
    procedureTableProps: {
      columns: columnList,
      columnsSearch: columnsSearch,
      data: tableData,
      headerUpdateCallBack: headerUpdateCallBack,
      sortedItem: currentSortItem,
      handleSortChange: handleSortChange,
      isLoading: advancedSearchloading,
      noOptions: true,
      onEndReached: onEndReached,
      isFetching: !!isFetchingNextPage,
      onRedirect: onClickToolTip,
      getHighlightedText,
    },
    search: {
      searchList,
      setSearchList,
      searchButtonClick,
    },
  };
}
